import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  BarChart3,
  LayoutDashboard,
  Mail,
  Settings,
  Store,
  Users,
} from "lucide-react";
import { AdminPermission } from "../api/generated/models/AdminPermission";
import { ROUTES } from "./routes";

export interface AdminNavLeaf {
  id: string;
  label: string;
  // The page's name where it is read outside its group: the permission picker
  // and the Admins list. Defaults to `label`; set it when that is ambiguous
  // ("All" -> "All partners").
  permissionLabel?: string;
  path: string;
  // Extra patterns that also light this row up. May contain `:param` segments.
  matchPaths?: string[];
  // undefined = visible to every admin.
  permission?: AdminPermission;
}

interface AdminNavItemBase {
  id: string;
  label: string;
  icon: LucideIcon;
}

export interface AdminNavLinkItem extends AdminNavItemBase {
  kind: "link";
  path: string;
  matchPaths?: string[];
  permission?: AdminPermission;
}

export interface AdminNavGroupItem extends AdminNavItemBase {
  kind: "group";
  // A group is visible when at least one child is; it carries no permission of its own.
  items: AdminNavLeaf[];
}

export type AdminNavItem = AdminNavLinkItem | AdminNavGroupItem;

export interface AdminNavSection {
  id: string;
  // undefined = no header, for the lone Dashboard row at the top.
  title?: string;
  items: AdminNavItem[];
}

// Who may open the routes matching `pattern` (and their descendants).
export interface AdminRouteRule {
  pattern: string;
  // undefined = every admin.
  permission?: AdminPermission;
}

// Super Admin is not a page: it implies every permission and only support can
// grant it. Every other permission belongs to exactly one sidebar row below.
export const SUPER_ADMIN_LABEL = "Super Admin";

// Each row's `permission` decides who sees it in the sidebar and who may open
// its routes (AdminRouteGuard). The groups and labels also lay out the
// permission picker on the Admins page, so a page is added in one place.
export const ADMIN_NAV: AdminNavSection[] = [
  {
    id: "root",
    items: [
      {
        kind: "link",
        id: "dashboard",
        label: "Dashboard",
        icon: LayoutDashboard,
        path: ROUTES.ADMIN_HOME(),
      },
    ],
  },
  {
    id: "operations",
    title: "Operations",
    items: [
      {
        kind: "group",
        id: "transactions",
        label: "Transactions",
        icon: ArrowLeftRight,
        items: [
          {
            id: "tx-all",
            label: "All",
            permissionLabel: "All transactions",
            path: ROUTES.ADMIN_TRANSACTIONS(),
            permission: AdminPermission.TRANSACTIONS,
          },
          {
            id: "tx-failed",
            label: "Failed",
            permissionLabel: "Failed transactions",
            path: ROUTES.ADMIN_FAILED_TRANSACTIONS(),
            permission: AdminPermission.FAILED_TRANSACTIONS,
          },
          {
            id: "tx-offline",
            label: "Offline",
            permissionLabel: "Offline transactions",
            path: ROUTES.ADMIN_SPECIAL_PARTNERS(),
            permission: AdminPermission.OFFLINE_TRANSACTIONS,
          },
        ],
      },
      {
        kind: "group",
        id: "customers",
        label: "Customers",
        icon: Users,
        items: [
          {
            id: "cu-users",
            label: "Users",
            path: ROUTES.ADMIN_USERS(),
            permission: AdminPermission.USERS,
          },
          {
            id: "cu-cashback",
            label: "Cashback requests",
            path: ROUTES.ADMIN_CASHBACK_REQUESTS(),
            permission: AdminPermission.CASHBACK_REQUESTS,
          },
          {
            id: "cu-brackets",
            label: "Points brackets",
            path: ROUTES.ADMIN_CUSTOMER_BRACKETS(),
            permission: AdminPermission.TOP_CUSTOMERS,
          },
          {
            id: "cu-erasures",
            label: "Erasures",
            path: ROUTES.ADMIN_ERASURES(),
            permission: AdminPermission.ERASURES,
          },
        ],
      },
      {
        kind: "group",
        id: "partners",
        label: "Partners",
        icon: Store,
        items: [
          {
            id: "pa-all",
            label: "All",
            permissionLabel: "All partners",
            path: ROUTES.ADMIN_PARTNER_MANAGEMENT(),
            // Detail routes are not descendants of the list route, so they must be declared.
            matchPaths: [
              ROUTES.ADMIN_PARTNER_DETAILS(),
              ROUTES.ADMIN_PARTNER_APPLICATION_DETAILS(),
            ],
            permission: AdminPermission.PARTNER_MANAGEMENT,
          },
          {
            id: "pa-featured",
            label: "Featured",
            path: ROUTES.ADMIN_FEATURED_PARTNERS(),
            permission: AdminPermission.FEATURED_PARTNERS,
          },
          {
            id: "pa-top",
            label: "Top merchants",
            path: ROUTES.ADMIN_TOP_MERCHANTS(),
            permission: AdminPermission.TOP_MERCHANTS,
          },
          {
            id: "pa-network",
            label: "Network report",
            path: ROUTES.ADMIN_PARTNER_NETWORK_REPORT(),
            permission: AdminPermission.PARTNER_NETWORK_REPORT,
          },
          {
            id: "pa-visa",
            label: "Visa",
            path: ROUTES.ADMIN_VISA(),
            matchPaths: [ROUTES.ADMIN_VISA_TRANSACTION()],
            permission: AdminPermission.VISA,
          },
          {
            id: "pa-promo-codes",
            label: "Promo codes",
            path: ROUTES.ADMIN_PROMO_CODES(),
            permission: AdminPermission.PROMO_CODES,
          },
          {
            id: "pa-opt-in-offers",
            label: "Opt-in Offers",
            path: ROUTES.ADMIN_OPT_IN_OFFERS(),
            permission: AdminPermission.OPT_IN_OFFERS,
          },
        ],
      },
    ],
  },
  {
    id: "insights",
    title: "Insights",
    items: [
      {
        kind: "group",
        id: "analytics",
        label: "Analytics",
        icon: BarChart3,
        items: [
          {
            id: "an-activity",
            label: "Activity",
            path: ROUTES.ADMIN_ANALYTICS_PLUS(),
            permission: AdminPermission.ANALYTICS,
          },
          {
            id: "an-zero-result-suggestions",
            label: "Zero result suggestion",
            permissionLabel: "Search suggestions",
            path: ROUTES.ADMIN_ZERO_RESULT_SUGGESTIONS(),
            permission: AdminPermission.SEARCH_SUGGESTIONS,
          },
          {
            id: "an-performance",
            label: "App performance",
            path: ROUTES.ADMIN_APP_PERFORMANCE(),
            permission: AdminPermission.APP_PERFORMANCE,
          },
          {
            id: "an-kpi",
            label: "KPI report",
            path: ROUTES.ADMIN_KPI_REPORT(),
            permission: AdminPermission.KPIREPORT,
          },
          {
            id: "an-revenue",
            label: "Revenue sharing",
            path: ROUTES.ADMIN_REVENUE_SHARING(),
            permission: AdminPermission.REVENUE_SHARING_REPORT,
          },
        ],
      },
      {
        kind: "group",
        id: "communication",
        label: "Communication",
        icon: Mail,
        items: [
          {
            id: "co-campaigns",
            label: "Campaigns",
            permissionLabel: "Campaign statistics",
            path: ROUTES.ADMIN_COMMUNICATION(),
            permission: AdminPermission.INSIGHTS,
          },
          {
            id: "co-send-push",
            label: "Send push",
            path: ROUTES.ADMIN_NOTIFICATIONS(),
            permission: AdminPermission.NOTIFICATIONS,
          },
          {
            id: "co-popup-campaigns",
            label: "Pop-up Campaigns",
            permissionLabel: "Pop-up campaigns",
            path: ROUTES.ADMIN_POPUP_CAMPAIGNS(),
            permission: AdminPermission.POPUP_CAMPAIGNS,
          },
        ],
      },
    ],
  },
  {
    id: "admin",
    title: "Admin",
    items: [
      {
        kind: "group",
        id: "settings",
        label: "Settings",
        icon: Settings,
        items: [
          {
            id: "se-admins",
            label: "Admins",
            path: ROUTES.ADMIN_MANAGEMENT(),
            permission: AdminPermission.ADMIN_MANAGEMENT,
          },
          {
            id: "se-invoicing",
            label: "Invoicing",
            path: ROUTES.ADMIN_INVOICING(),
            permission: AdminPermission.BILLING,
          },
          {
            id: "se-webshop",
            label: "Webshop",
            path: ROUTES.ADMIN_WEBSHOP(),
            permission: AdminPermission.WEBSHOP,
          },
        ],
      },
    ],
  },
];

// Admin routes without a sidebar row. The route guard refuses any admin path it
// has no rule for, so every such route has to be declared here.
export const UNLISTED_ADMIN_ROUTES: AdminRouteRule[] = [
  // Preview of the app's Typesense search. It reads nothing from the backend,
  // so it stays open to every admin, as it always was.
  { pattern: ROUTES.ADMIN_SEARCH_PREVIEW() },
  // Preview of the KPI report with corrected active users, ahead of the switch.
  // Super Admin only, and deliberately without a sidebar row or any link.
  { pattern: ROUTES.ADMIN_KPI_REPORT_V2(), permission: AdminPermission.SUPER_ADMIN },
];
